import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { OrganogramaItem } from "../../../../interfaces/ClienteEquipe";
import { definirGestor, getOrganograma } from "../../../../services/clienteEquipeApi";
import { mensagemDoServidor } from "../../../../utils/mensagemDoServidor";
import { montarArvore, NoOrganograma } from "./organogramaTree";

const selectCls =
  "rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";

const SEM_GESTAO = "Você não gere a equipe deste cliente.";

interface Props {
  orgId: number;
  /** `cliente.equipe.gerir`. Sem ele o organograma é só leitura. */
  podeGerir: boolean;
}

/** Aba "Organograma" do cliente: árvore indentada montada da lista plana; quem gere troca o gestor. */
const OrganogramaTab: React.FC<Props> = ({ orgId, podeGerir }) => {
  const [itens, setItens] = useState<OrganogramaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    getOrganograma(orgId)
      .then(setItens)
      .catch((e) => setErro(mensagemDoServidor(e, e?.response?.status === 403 ? SEM_GESTAO : "Não foi possível carregar o organograma. Tente de novo em instantes.")))
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(carregar, [carregar]);

  const trocarGestor = async (vinculoId: number, valor: string) => {
    setOcupado(true);
    try {
      await definirGestor(orgId, vinculoId, valor === "" ? null : Number(valor));
      toast.success("Gestor atualizado.");
      carregar();
    } catch (e) {
      toast.error(mensagemDoServidor(e, "Não foi possível trocar o gestor."));
    } finally {
      setOcupado(false);
    }
  };

  const renderNo = (no: NoOrganograma): JSX.Element => (
    <li key={no.item.vinculoId}>
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 dark:border-white/5 dark:bg-navy-900">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-navy-700 dark:text-white">{no.item.nome}</p>
          <p className="truncate text-xs text-gray-600 dark:text-gray-400">
            {no.item.perfil ? `${no.item.perfil} · ` : ""}{no.item.email}
          </p>
        </div>
        {podeGerir && (
          <select
            aria-label={`Gestor de ${no.item.nome}`}
            className={selectCls}
            value={no.item.gestorVinculoId ?? ""}
            disabled={ocupado}
            onChange={(e) => trocarGestor(no.item.vinculoId, e.target.value)}
          >
            <option value="">Sem gestor</option>
            {itens
              .filter((o) => o.vinculoId !== no.item.vinculoId)
              .map((o) => (
                <option key={o.vinculoId} value={o.vinculoId}>{o.nome}</option>
              ))}
          </select>
        )}
      </div>
      {no.filhos.length > 0 && (
        <ul className="ml-5 mt-2 flex flex-col gap-2 border-l border-gray-200 pl-3 dark:border-white/10">
          {no.filhos.map(renderNo)}
        </ul>
      )}
    </li>
  );

  if (loading) {
    return <div role="status" className="py-12 text-center text-sm text-gray-700 dark:text-gray-300">Carregando…</div>;
  }
  if (erro) {
    return (
      <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">
        {erro}
      </p>
    );
  }
  if (itens.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-gray-200 py-10 text-center text-sm text-gray-700 dark:border-white/10 dark:text-gray-300">
        Este cliente ainda não tem pessoas com acesso. Quando houver, o organograma aparece aqui.
      </p>
    );
  }

  return (
    <ul aria-label="Organograma do cliente" className="flex flex-col gap-2">
      {montarArvore(itens).map(renderNo)}
    </ul>
  );
};

export default OrganogramaTab;
